import { useLayoutEffect, useRef, useState } from "react";
import styles from "./Announcer.module.css";
import { messageBoxAtTop, messagesBox, shownBox } from "./placement";

// Announced messages (v1.3 ticket 03, RCMC-17). Replaces the 5-second AlertPopUp toasts.
//
// Two live regions, always in the DOM and empty until a message arrives (a screen reader only
// announces changes inside a region it already knows about): a polite status region for
// confirmations and an assertive alert region for errors. App renders this beside the app root,
// never inside it, so it keeps announcing while the root is inert behind a dialog.
//
// Each message is { id, text } or null. The inner element is keyed by id, so a new message (even
// one with the same text) is a fresh insertion, which screen readers announce again. A message stays
// until its Dismiss button is pressed or the next message replaces it. Dismiss sits outside the live
// element, so "Dismiss" is never read out as part of the message.
const Message = ({ role, message, className, onDismiss }) => (
  <div className={message ? `${styles.message} ${className}` : undefined}>
    <div role={role} className={styles.text}>{message && <p key={message.id}>{message.text}</p>}</div>
    {message && (
      <button type="button" className={styles.dismiss} onClick={onDismiss}>
        Dismiss
      </button>
    )}
  </div>
);

/** @param {{status, alert, onDismiss: (region: "status" | "alert") => void}} props */
const Announcer = ({ status, alert, onDismiss }) => {
  const boxRef = useRef(null);
  const [atTop, setAtTop] = useState(false);

  // Ticket 15 (WCAG 2.4.11): while a message shows, keep the box off the control that has focus (placement.js). Checked
  // when a message arrives or changes, whenever focus moves (again a frame later, once the browser has scrolled the
  // control into view) and when the window resizes or zooms. Where the box is now is read from the page
  // (data-placement), so checks that run before React has re-rendered agree with each other. With no message, it goes
  // back to the bottom.
  const hasMessage = Boolean(status || alert);
  useLayoutEffect(() => {
    if (!hasMessage) {
      setAtTop(false);
      return undefined;
    }
    let frame = 0;
    const place = () => {
      const box = boxRef.current;
      const focused = document.activeElement;
      if (!box || !focused || focused === document.body || box.contains(focused)) return;
      const atTopNow = box.getAttribute("data-placement") === "top";
      setAtTop(messageBoxAtTop({
        focus: shownBox(focused),
        box: messagesBox(box),
        viewportHeight: document.documentElement.clientHeight, // without a horizontal scrollbar, as `bottom: 10px`
        atTop: atTopNow,
      }));
    };
    const onFocusMoved = () => {
      place();
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(place);
    };
    place();
    document.addEventListener("focusin", onFocusMoved);
    window.addEventListener("resize", place);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("focusin", onFocusMoved);
      window.removeEventListener("resize", place);
    };
  }, [hasMessage, status, alert]);

  return (
    <div ref={boxRef} data-placement={atTop ? "top" : "bottom"} className={styles.announcer}>
      <Message role="alert" message={alert} className={styles.error} onDismiss={() => onDismiss("alert")} />
      <Message role="status" message={status} className={styles.confirmation} onDismiss={() => onDismiss("status")} />
    </div>
  );
};

export default Announcer;
